/**
 * StatBar - HUD progress bar tuned for shield/mana/stamina/energy. Has an icon
 * slot left of the bar and a label/value row above the track.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export type StatBarTheme = 'shield' | 'mana' | 'stamina' | 'energy';

export interface StatBarOptions extends BaseUIComponentOptions {
  /** Current value. */
  value?: number;
  /** Maximum value. */
  maxValue?: number;
  /** Color theme matching the HUD palette. */
  theme?: StatBarTheme;
  /** SVG markup injected into the icon slot. */
  iconHtml?: string;
  /** Label text shown above the bar. */
  label?: string;
}

export class StatBar extends BaseUIComponent<StatBarOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Stat Bar',
    category: 'hud',
    summary: 'HUD progress bar for shield, mana, stamina, and energy with icon slot.',
    useCases: ['shield', 'mana', 'stamina', 'energy', 'hud bar', 'stat bar', 'health bar'],
    optionsType: 'StatBarOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/stat-bar.html',
      styles: '@engine/assets/ui/styles/stat-bar.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private iconElement: HTMLElement | null = null;
  private labelElement: HTMLElement | null = null;
  private valueElement: HTMLElement | null = null;
  private fillElement: HTMLElement | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: StatBar.metadata.assetPaths.template,
      stylesPath: StatBar.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<StatBarOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
      value: 100,
      maxValue: 100,
      theme: 'mana',
      iconHtml: '',
      label: '',
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      theme: this.options.theme,
      label: this.options.label,
      value: `${this.options.value}/${this.options.maxValue}`,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-stat-bar') as HTMLElement | null;
    this.iconElement = this.layout.querySelector('[data-stat-bar-icon]') as HTMLElement | null;
    this.labelElement = this.layout.querySelector('[data-stat-bar-label]') as HTMLElement | null;
    this.valueElement = this.layout.querySelector('[data-stat-bar-value]') as HTMLElement | null;
    this.fillElement = this.layout.querySelector('[data-stat-bar-fill]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    this.applyFill();
    if (this.options.iconHtml) this.applyIcon();
  }

  /** Set the current value and update the fill width. */
  public setValue(value: number): void {
    this.options.value = value;
    this.applyFill();
  }

  /** Set the maximum value and update the fill width. */
  public setMaxValue(maxValue: number): void {
    this.options.maxValue = maxValue;
    this.applyFill();
  }

  /** Switch the color theme. */
  public setTheme(theme: StatBarTheme): void {
    if (!this.rootElement) return;
    this.rootElement.classList.remove(`theme-${this.options.theme}`);
    this.options.theme = theme;
    this.rootElement.classList.add(`theme-${theme}`);
  }

  /** Inject SVG markup into the icon slot. */
  public setIconHtml(svg: string): void {
    this.options.iconHtml = svg;
    this.applyIcon();
  }

  /** Update the label text shown above the track. */
  public setLabel(text: string): void {
    this.options.label = text;
    if (this.labelElement) this.labelElement.textContent = text;
  }

  private applyFill(): void {
    const pct = this.options.maxValue > 0
      ? Math.min(100, Math.max(0, (this.options.value / this.options.maxValue) * 100))
      : 0;
    if (this.fillElement) this.fillElement.style.width = `${pct}%`;
    if (this.valueElement) {
      this.valueElement.textContent = `${Math.round(this.options.value)}/${Math.round(this.options.maxValue)}`;
    }
  }

  private applyIcon(): void {
    if (this.iconElement) this.iconElement.innerHTML = this.options.iconHtml;
  }

  protected override onDestroy(): void {
    this.rootElement = null;
    this.iconElement = null;
    this.labelElement = null;
    this.valueElement = null;
    this.fillElement = null;
  }
}
